import { createClient } from '@supabase/supabase-js'
import { isAuthDebugEnabled } from '@/lib/debug'

// Supabase configuration
// IMPORTANT: These values MUST be set in .env file for local development
// and in Vercel environment variables for production
export const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? ''
export const supabaseAnonKey =
  import.meta.env.VITE_SB_PUBLISHABLE_KEY ??
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ??
  import.meta.env.VITE_SUPABASE_ANON_KEY ??
  ''
export const supabaseFunctionsUrl =
  import.meta.env.VITE_SUPABASE_FUNCTIONS_URL ||
  (supabaseUrl ? `${supabaseUrl}/functions/v1` : undefined)

function decodeBase64UrlJson(input: string): Record<string, unknown> | null {
  try {
    const normalized = input.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)
    return JSON.parse(atob(padded)) as Record<string, unknown>
  } catch {
    return null
  }
}

function deriveSupabaseBaseFromPublishableKey(key: string): string {
  const parts = String(key || '').split('.')
  if (parts.length < 2) return ''
  const payload = decodeBase64UrlJson(parts[1])
  const iss = typeof payload?.iss === 'string' ? payload.iss.trim() : ''
  if (!iss) return ''
  try {
    const url = new URL(iss)
    return `${url.protocol}//${url.host}`
  } catch {
    return ''
  }
}

// Validate required environment variables only in the browser (avoids 500 when
// Vite or other tooling loads this module without env, e.g. during transform).
if (typeof window !== 'undefined') {
  if (!supabaseUrl || !supabaseAnonKey || !supabaseFunctionsUrl) {
    console.error('❌ Missing Supabase credentials!')
    console.error('   VITE_SUPABASE_URL:', supabaseUrl ? '✓' : '✗ MISSING')
    console.error('   VITE_SUPABASE_ANON_KEY:', supabaseAnonKey ? '✓' : '✗ MISSING')
    console.error('   VITE_SUPABASE_FUNCTIONS_URL:', supabaseFunctionsUrl ? '✓' : '✗ MISSING')
    console.error('')
    console.error('Please ensure these are set in:')
    console.error('  - Local: .env in the app directory')
    console.error('  - Production: Vercel environment variables')
    throw new Error('Supabase configuration is incomplete')
  }
}

// Ensure URL is properly formatted (remove trailing slash if present).
// Use placeholder when missing so module can load in Node (e.g. Vite transform).
const configuredUrl = supabaseUrl
  ? supabaseUrl.endsWith('/')
    ? supabaseUrl.slice(0, -1)
    : supabaseUrl
  : 'https://placeholder.supabase.co'
const keyDerivedUrl = deriveSupabaseBaseFromPublishableKey(supabaseAnonKey)
const normalizedUrl =
  keyDerivedUrl &&
  configuredUrl !== 'https://placeholder.supabase.co' &&
  configuredUrl !== keyDerivedUrl
    ? keyDerivedUrl
    : configuredUrl

if (
  typeof window !== 'undefined' &&
  keyDerivedUrl &&
  configuredUrl !== 'https://placeholder.supabase.co' &&
  configuredUrl !== keyDerivedUrl
) {
  console.warn('[supabase] Host/key mismatch detected; using key-derived host', {
    configuredUrl,
    keyDerivedUrl,
  })
}

if (typeof window !== 'undefined' && supabaseUrl) {
  try {
    new URL(normalizedUrl)
  } catch {
    throw new Error(`Invalid Supabase URL format: ${normalizedUrl}`)
  }
}

// Extract project ref from URL (e.g., https://yvtdmrysnaptcyoorthj.supabase.co -> yvtdmrysnaptcyoorthj)
const projectRef = normalizedUrl.match(/https?:\/\/([^.]+)\.supabase\.co/)?.[1]
const configuredEnvironment = import.meta.env.VITE_ENVIRONMENT ?? ''
const PRODUCTION_PROJECT_REF = 'yvtdmrysnaptcyoorthj'
const isProdMode = import.meta.env.PROD

// Clean up old Supabase auth tokens from localStorage (prevents stale connections).
if (typeof window !== 'undefined' && projectRef) {
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key?.startsWith('sb-') || !key.endsWith('-auth-token')) continue
      const isCurrentProject = key.includes(projectRef)
      if (!isCurrentProject) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key)
      if (isAuthDebugEnabled()) console.log('[supabase] Removed auth token from localStorage:', key)
    })
  } catch (error) {
    console.warn('[supabase] Failed to clean up old tokens:', error)
  }
}

// Debug: Log the URL being used only when VITE_DEBUG_AUTH is set
if (isAuthDebugEnabled()) {
  console.log('[supabase] Initializing client with URL:', normalizedUrl)
  console.log('[supabase] Project ref:', projectRef)
}

if (typeof window !== 'undefined' && configuredEnvironment === 'production' && projectRef) {
  if (projectRef !== PRODUCTION_PROJECT_REF) {
    console.warn('[supabase] Environment/project mismatch detected', {
      environment: configuredEnvironment,
      expectedProjectRef: PRODUCTION_PROJECT_REF,
      actualProjectRef: projectRef,
    })
  }
}

if (typeof window !== 'undefined' && projectRef) {
  if (isProdMode && projectRef !== PRODUCTION_PROJECT_REF) {
    console.warn('[supabase] Production build is not targeting production project', {
      expectedProjectRef: PRODUCTION_PROJECT_REF,
      actualProjectRef: projectRef,
    })
  }
}

// Detect dev environments for cookie configuration
const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
const isDevEnvironment =
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname.includes('.github.dev') ||
  hostname.includes('.gitpod.io') ||
  hostname.includes('.codespaces') ||
  hostname.includes('localhost.run') ||
  hostname.includes('.preview.app')

if (isAuthDebugEnabled()) {
  console.log(
    '[supabase] Cookie config - hostname:',
    hostname,
    'isDevEnvironment:',
    isDevEnvironment,
  )
}

let allowPasswordGrantUntilMs = 0
const realFetch = typeof window !== 'undefined' ? window.fetch.bind(window) : fetch
// Ensure Supabase requests have apikey (fixes "No API key found"). Do NOT set Authorization here:
// REST requests must use the user's JWT so RLS allows access to accounts/user_preferences.
function ensureSupabaseKey(input: RequestInfo | URL, init?: RequestInit): RequestInit | undefined {
  const urlStr =
    typeof input === 'string' ? input : input instanceof Request ? input.url : (input as URL).href
  if (!urlStr.includes(normalizedUrl)) return init
  const existingHeaders = input instanceof Request ? input.headers : init?.headers
  const headers = new Headers(existingHeaders)
  if (!headers.has('apikey')) headers.set('apikey', supabaseAnonKey)
  return { ...init, headers }
}
const supabaseFetch: typeof fetch =
  typeof window !== 'undefined'
    ? async (input: RequestInfo | URL, init?: RequestInit) => {
        const initWithKey = ensureSupabaseKey(input, init)
        return realFetch(input, initWithKey)
      }
    : fetch
// Patch global fetch so auth client cannot bypass our interceptor and so Supabase requests always have apikey
if (typeof window !== 'undefined') {
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const initWithKey = ensureSupabaseKey(input, init)
    return realFetch(input, initWithKey)
  }
}

// Create Supabase client.
// When env is missing in Node (e.g. Vite transform), use placeholder so the module can load.
const clientAnonKey = supabaseAnonKey || 'placeholder-key-for-build'
// NOTE: Realtime is temporarily disabled due to WebSocket connection issues
// The realtime URL should be automatically derived from supabaseUrl, but it's
// currently trying to connect to localhost instead of the correct Supabase URL
export const supabase = createClient(normalizedUrl, clientAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Passkeys disabled until enabled in Supabase Dashboard → Authentication → Passkeys.
    // experimental: { passkey: true },
  },
  // Realtime configuration - temporarily minimal to prevent localhost connections
  // TODO: Re-enable once Supabase realtime URL issue is resolved
  realtime: {
    // Disable realtime entirely for now to prevent WebSocket connection errors
    // This prevents the client from trying to connect to localhost
    // The app will work fine without realtime - it just won't have live updates
  },
  global: {
    headers: {
      'x-client-info': 'lina-web',
    },
    fetch: typeof window !== 'undefined' ? supabaseFetch : undefined,
  },
})

// Guard: never send signInWithPassword to Supabase with empty/invalid credentials (prevents 400)
if (typeof window !== 'undefined') {
  const realSignInWithPassword = supabase.auth.signInWithPassword.bind(supabase.auth)
  supabase.auth.signInWithPassword = async (credentials: { email?: string; password?: string }) => {
    const email = credentials?.email ?? ''
    const password = credentials?.password ?? ''
    const trimmedEmail = typeof email === 'string' ? email.trim().toLowerCase() : ''
    if (!trimmedEmail || !password || typeof password !== 'string' || password.length === 0) {
      return {
        data: { user: null, session: null },
        error: {
          message: 'Email and password are required.',
          name: 'AuthApiError',
          status: 400,
        } as import('@supabase/supabase-js').AuthError,
      }
    }
    return realSignInWithPassword({ email: trimmedEmail, password })
  }
}

// Helper function to get auth token for user-scoped Supabase operations.
// Returns null when there is no session — never returns the anon key as a stand-in for a user JWT.
export const getAuthToken = async (): Promise<string | null> => {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession()

    if (error) {
      console.error('Error getting session:', error)

      // Handle invalid refresh token - sign out
      if (
        error.message?.includes('Invalid Refresh Token') ||
        error.message?.includes('Refresh Token Not Found')
      ) {
        console.log('[getAuthToken] Invalid refresh token detected, signing out...')
        await supabase.auth.signOut()
        return null
      }

      return null
    }

    if (!session) {
      return null
    }

    return session.access_token
  } catch (error) {
    console.error('Failed to get auth token:', error)
    return null
  }
}

/**
 * Sign in with email/password: validates input (avoids 400 from empty/invalid body).
 */
export async function signInWithPassword(email: string, password: string) {
  const trimmedEmail = typeof email === 'string' ? email.trim().toLowerCase() : ''
  if (!trimmedEmail) {
    return {
      data: { user: null, session: null },
      error: {
        message: 'Please enter your email address.',
      } as import('@supabase/supabase-js').AuthError,
    }
  }
  if (!password || typeof password !== 'string' || password.length === 0) {
    return {
      data: { user: null, session: null },
      error: {
        message: 'Please enter your password.',
      } as import('@supabase/supabase-js').AuthError,
    }
  }
  allowPasswordGrantUntilMs = Date.now() + 15_000
  const { data, error } = await supabase.auth.signInWithPassword({
    email: trimmedEmail,
    password,
  })
  if (error) return { data: { user: null, session: null }, error }
  return { data, error: null }
}

/**
 * Always clear local auth state, even when network requests fail.
 * This prevents "logged out but still appears logged in" loops.
 */
export async function forceLocalSignOut(): Promise<void> {
  const { error } = await supabase.auth.signOut({ scope: 'local' })
  if (error) {
    console.warn('[forceLocalSignOut] Local sign-out reported error:', error.message)
  }
}

// Helper function to sign up (for testing)
export const signUp = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { signup_source: 'app' },
    },
  })
  return { data, error }
}

// Helper function to initialize authentication
export const initializeAuth = async () => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()

    return session
  } catch (error) {
    console.error('Auth initialization failed:', error)
    return null
  }
}

// Get current user info
export const getCurrentUser = async () => {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser()
  if (error) {
    console.error('Error getting user:', error)
    return null
  }
  return user
}

// Export configuration for debugging
export const config = {
  url: supabaseUrl,
  normalizedUrl: normalizedUrl,
  projectRef: projectRef,
  anonKey: supabaseAnonKey ? supabaseAnonKey.substring(0, 20) + '...' : 'not set',
}

// Helper to clear all Supabase-related storage (cookies + localStorage) for debugging/sign-out
export function clearSupabaseCache() {
  if (typeof window === 'undefined') return

  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key?.startsWith('sb-')) keysToRemove.push(key)
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key))

  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const eq = cookie.trim().indexOf('=')
    if (eq === -1) continue
    const name = cookie.trim().slice(0, eq).trim()
    if (name.startsWith('sb-')) {
      document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Strict`
    }
  }
  console.log('[supabase] Cleared Supabase cache (localStorage + cookies)')
}
